/*
 * GENERATED FILE — DO NOT EDIT BY HAND.
 *
 * The 20-icon stroke set extracted from the delivered Phase-1 design
 * ("Kalqy Foundations", section 04 · Icons), plus any icon listed in
 * EXTRA_ICONS in the generator — see the note there for why those exist.
 * Produced by scripts/generate-icons.mjs.
 * Regenerate with:  npm run icons:generate
 * CI fails if this file drifts:  npm run icons:check
 *
 * 🔴 ONE STROKE SET, SHARED BY BOTH SHELLS (design-system.md §6).
 * 2px round-cap strokes on a 24px grid. KidShell renders THE SAME PATHS at
 * 40–56px with 2.6px strokes — the stroke thickens, the geometry does not
 * change. That is why `size` and `strokeWidth` are props rather than two
 * separate icon sets.
 *
 * 🔴 EMOJI ARE NOT ICONS. They identify the games and nothing else. If you
 * are reaching for an emoji in a UI slot, the answer is one of these.
 */
import type { SVGProps } from 'react'

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** 14–24px in DashboardShell, 40–56px in KidShell. */
  size?: number
  /** 2 in DashboardShell, 2.6 in KidShell. */
  strokeWidth?: number
}

/** Shared wrapper so every icon carries identical stroke geometry. */
function Icon({ size = 24, strokeWidth = 2, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  )
}

/** `check` */
export const CheckIcon = (props: IconProps) => (
  <Icon {...props}><path d="M20 6 9 17l-5-5"></path></Icon>
)

/** `alert` */
export const AlertIcon = (props: IconProps) => (
  <Icon {...props}><path d="M12 4l9 16H3L12 4Z"></path><path d="M12 10v4M12 17h.01"></path></Icon>
)

/** `info` */
export const InfoIcon = (props: IconProps) => (
  <Icon {...props}><circle cx="12" cy="12" r="9"></circle><path d="M12 8h.01M11 12h1v5h1"></path></Icon>
)

/** `camera` */
export const CameraIcon = (props: IconProps) => (
  <Icon {...props}><path d="M3 8a2 2 0 0 1 2-2h3l1.5-2h5L16 6h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8Z"></path><circle cx="12" cy="12.5" r="3.2"></circle></Icon>
)

/** `camera-off` */
export const CameraOffIcon = (props: IconProps) => (
  <Icon {...props}><path d="M3 8a2 2 0 0 1 2-2h3M21 12v5a2 2 0 0 1-2 2H7"></path><path d="M3 12v5a2 2 0 0 0 2 2M14.5 4l1.5 2h3a2 2 0 0 1 2 2"></path><path d="M4 4l16 16"></path></Icon>
)

/** `chevron` */
export const ChevronIcon = (props: IconProps) => (
  <Icon {...props}><path d="M9 18l6-6-6-6"></path></Icon>
)

/** `plus` */
export const PlusIcon = (props: IconProps) => (
  <Icon {...props}><path d="M12 5v14M5 12h14"></path></Icon>
)

/** `download` */
export const DownloadIcon = (props: IconProps) => (
  <Icon {...props}><path d="M12 4v11M7 11l5 5 5-5M4 20h16"></path></Icon>
)

/** `children` */
export const ChildrenIcon = (props: IconProps) => (
  <Icon {...props}><circle cx="9" cy="8" r="3"></circle><path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5"></path><path d="M16 6a3 3 0 0 1 0 6M18 20c0-2.4-.8-4-2-5"></path></Icon>
)

/** `reports` */
export const ReportsIcon = (props: IconProps) => (
  <Icon {...props}><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5V5.5Z"></path><path d="M8 8h8"></path></Icon>
)

/** `trend-up` */
export const TrendUpIcon = (props: IconProps) => (
  <Icon {...props}><path d="M4 17l6-6 4 4 6-8"></path><path d="M14 7h6v6"></path></Icon>
)

/** `trend-down` */
export const TrendDownIcon = (props: IconProps) => (
  <Icon {...props}><path d="M4 7l6 6 4-4 6 8"></path><path d="M20 11v6h-6"></path></Icon>
)

/** `target` */
export const TargetIcon = (props: IconProps) => (
  <Icon {...props}><circle cx="12" cy="12" r="8"></circle><circle cx="12" cy="12" r="3.4"></circle></Icon>
)

/** `point` */
export const PointIcon = (props: IconProps) => (
  <Icon {...props}><path d="M9 11V5a1.6 1.6 0 0 1 3.2 0v6"></path><path d="M12.2 11V6.4a1.6 1.6 0 0 1 3.2 0V12"></path><path d="M15.4 12v-1a1.6 1.6 0 0 1 3.2 0v4.5A5.5 5.5 0 0 1 13 21h-1a6 6 0 0 1-6-6v-2.2a1.6 1.6 0 0 1 3.2 0"></path></Icon>
)

/** `sticker` */
export const StickerIcon = (props: IconProps) => (
  <Icon {...props}><path d="M12 3l2.2 5.4L20 9.2l-4 4 1 5.8-5-2.9-5 2.9 1-5.8-4-4 5.8-.8L12 3Z"></path></Icon>
)

/** `play` */
export const PlayIcon = (props: IconProps) => (
  <Icon {...props}><path d="M8 5v14l11-7L8 5Z"></path></Icon>
)

/** `voice` */
export const VoiceIcon = (props: IconProps) => (
  <Icon {...props}><path d="M11 5 6 9H3v6h3l5 4V5Z"></path><path d="M16 9.5a3.5 3.5 0 0 1 0 5M18.5 7a7 7 0 0 1 0 10"></path></Icon>
)

/** `privacy` */
export const PrivacyIcon = (props: IconProps) => (
  <Icon {...props}><rect x="3" y="11" width="18" height="10" rx="2.5"></rect><path d="M7.5 11V8a4.5 4.5 0 0 1 9 0v3"></path></Icon>
)

/** `grid` */
export const GridIcon = (props: IconProps) => (
  <Icon {...props}><rect x="4" y="4" width="7" height="7" rx="2"></rect><rect x="13" y="4" width="7" height="7" rx="2"></rect><rect x="4" y="13" width="7" height="7" rx="2"></rect><rect x="13" y="13" width="7" height="7" rx="2"></rect></Icon>
)

/** `school` */
export const SchoolIcon = (props: IconProps) => (
  <Icon {...props}><path d="M6 20V10M6 10l7-6 5 4v12H6Z"></path><path d="M10 20v-5h4v5"></path></Icon>
)

/** `sign-out` */
export const SignOutIcon = (props: IconProps) => (
  <Icon {...props}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></Icon>
)

/** Every icon, keyed by its design-system name. */
export const ICONS = {
  'check': CheckIcon,
  'alert': AlertIcon,
  'info': InfoIcon,
  'camera': CameraIcon,
  'camera-off': CameraOffIcon,
  'chevron': ChevronIcon,
  'plus': PlusIcon,
  'download': DownloadIcon,
  'children': ChildrenIcon,
  'reports': ReportsIcon,
  'trend-up': TrendUpIcon,
  'trend-down': TrendDownIcon,
  'target': TargetIcon,
  'point': PointIcon,
  'sticker': StickerIcon,
  'play': PlayIcon,
  'voice': VoiceIcon,
  'privacy': PrivacyIcon,
  'grid': GridIcon,
  'school': SchoolIcon,
  'sign-out': SignOutIcon,
} as const

export type IconName = keyof typeof ICONS
